import { Form, Formik } from 'formik';
import { Props, Values } from './CodeBookItemsPrice.types';
import * as Yup from 'yup';
import FormField from '../../components/field/FormField';
import Button from '../../components/button/Button';
import { useTranslation } from '../../context/translationContext';
import SelectFilteredModalTable from '../../components/select/SelectFilteredModalTable';
import { useEffect, useRef, useState } from 'react';
import { VatCode } from '../../types/VatCode';
import { useCurrentCompany } from '../../hooks/useCompany';
import { Loader } from '../../components/loader/Loader';
import { columns } from '../../components/columns/Company/VatCodesColumn';
import { DirectionDoc } from '../../types/Invoice';
import { apiUrl } from '../../api/apiUrl';
import http from '../../api/axios';
import { Currency } from '../../types/Currency';
import { dateSubmitFormat } from '../../types/core/date.types';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from '../../pages/RouterContainer';

/**
 *  Form component for creating / editing code book item price
 */
export const CodeBookItemsPriceForm = ({ onSubmit, initialValues }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const formRef = useRef<any>();
  const { company } = useCurrentCompany();
  const [vatCodes, setVatCodes] = useState<VatCode[] | undefined>();
  const [selectedVatCode, setSelectedVatCode] = useState<VatCode | undefined>();
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [currencies, setCurrencies] = useState<Currency[] | null>(null);

  /**
   * Loads currencies by date of taxable transaction
   */
  const fetchCurrenciesData = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.CURRENCY_GET_LIST}?date=${format(new Date(), dateSubmitFormat)}`,
    });
    if (response?.data) setCurrencies(response?.data);
  };
  /**
   * Opens modal table
   */
  const handleOpenModalTable = () => {
    setIsTableOpen(true);
  };
  /**
   * Closes modal table
   */
  const handleCloseModalTable = () => setIsTableOpen(false);

  /**
   * Loads vat codes for select2
   */
  const fetchVatCodes = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.VAT_CODE_GET_LIST_FILTERED}?direction=${DirectionDoc.OUTPUT_DOC}`,
    });
    if (response?.data) {
      setVatCodes(response?.data);
    }
  };

  useEffect(() => {
    fetchVatCodes();
    fetchCurrenciesData();
  }, []);

  /**
   * pre-fill code if exists
   */
  useEffect(() => {
    if (!vatCodes) return;
    if (initialValues?.vatCodeId)
      setSelectedVatCode(vatCodes.find((x: VatCode) => x.id === initialValues?.vatCodeId));
    if (initialValues?.vatCode) setSelectedVatCode(initialValues?.vatCode);
  }, [vatCodes]);

  const _initialValues: Values = {
    codeBookItemId: itemId ? Number(itemId) : undefined,
    validFrom: format(new Date(), dateSubmitFormat),
    vatCodeId: undefined,
    price: undefined,
    unit: '',
    currency: '',
  };

  // required fields
  const CodeBookItemSchema = Yup.object().shape({
    validFrom: Yup.string().required('Date valid from is required.'),
  });

  /**
   * VAT code vas selected - set to state and close modal
   */
  const onItemVATCodeChange = (val: any) => {
    setSelectedVatCode(val);
    setIsTableOpen(false);
  };

  useEffect(() => {
    if (!formRef || !formRef.current || !initialValues || !initialValues.validFrom) return;
    formRef.current.setFieldValue('validFrom', initialValues.validFrom);
  }, [formRef, formRef.current, initialValues]);
  if (!company || !currencies) return <Loader />;
  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={initialValues || _initialValues}
      onSubmit={onSubmit}
      validationSchema={CodeBookItemSchema}
    >
      {({ errors, touched, setFieldValue }) => (
        <Form className="w-full text-center">
          <div className="my-4">
            <FormField
              id="validFrom"
              name="validFrom"
              label={t('page.code-book-item-price.input-valid-from')}
              type="date"
              errors={errors}
              touched={touched}
            />
          </div>
          {vatCodes && (
            <span
              className="border border-gray-600 text-gray-900 text-sm rounded-lg focus:border-gray-500 block w-full focus:border-gray-600 pl-2"
              style={{ display: 'inline-flex' }}
            >
              {selectedVatCode?.coefficientPercent !== undefined &&
                selectedVatCode?.coefficientPercent !== null && (
                  <div className="mr-2 pt-2.5 pb-2.5 pr-0" onClickCapture={handleOpenModalTable}>
                    {selectedVatCode.code + ':'}
                  </div>
                )}
              <SelectFilteredModalTable
                data={vatCodes ?? []}
                columns={columns}
                open={isTableOpen}
                onClose={() => handleCloseModalTable()}
                title={t('page.code-book-item-price.input-vat-code')}
                value={
                  selectedVatCode?.coefficientPercent !== undefined &&
                  selectedVatCode?.coefficientPercent !== null
                    ? selectedVatCode?.coefficientPercent
                    : null
                }
                labelKey="code"
                valueKey="code"
                displayValue={'coefficientPercent'}
                onClick={handleOpenModalTable}
                onSelected={(val) => {
                  setFieldValue('vatCodeId', val?.id);
                  onItemVATCodeChange(val);
                }}
                onChange={(val) => {
                  setFieldValue('vatCodeId', val?.id);
                  onItemVATCodeChange(val);
                }}
                fulltextSearch
                placeholder={t('page.invoice.invoice-items.enter-vat')}
                inputClassName={`p-2.5 pl-0`}
                titleClassName={`-translate-x-[50pt]`}
                modalClassName={`left-[15%]`}
                showClearButton
              />
            </span>
          )}
          <div className="my-4">
            <FormField
              id="price"
              name="price"
              type="number"
              label={t('page.code-book-item-price.input-price')}
            />
          </div>
          <div className="my-4">
            <FormField id="unit" name="unit" label={t('page.code-book-item-price.input-unit')} />
          </div>
          <div className="my-4">
            <FormField
              type="select"
              id="currency"
              name="currency"
              label={t('page.code-book-item-price.input-currency')}
              showEmptyOption
            >
              {currencies.map((opt: Currency, index: number) => (
                <option key={index} value={opt.code}>
                  {opt.country + ' - ' + opt.code}
                </option>
              ))}
            </FormField>
          </div>
          <div className="flex float-left">
            <Button
              title={t('general.back')}
              color="gray"
              className="mr-3"
              onClick={() => navigate(`/${paths.CODE_BOOK_ITEMS}/edit/${itemId}`)}
            />
            <Button title={t('general.save')} color="green" type="submit" showLoading />
          </div>
        </Form>
      )}
    </Formik>
  );
};
